import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, IconButton, Typography, FormControl } from '@mui/material';
import Close from '@mui/icons-material/Close';
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../locales/translations';
import { getPictureURL, StorageType } from '../../../helpers/storage';

const MAX_IMAGES = 10;
const MAX_TOTAL_SIZE_MB = 40;

type ImagePickerProps = {
    images: File[];
    setImages: (images: File[]) => void;
    existingImagePaths?: string[];
    onExistingImagesChange?: (paths: string[]) => void;
};

const ImagePicker: React.FC<ImagePickerProps> = ({ images, setImages, existingImagePaths = [], onExistingImagesChange }) => {
    const { t } = useTranslation([Namespace.REPORT]);

    // Combined state for totalSizeMB and errorMessage
    const [pickerState, setPickerState] = useState<{ totalSizeMB: number; errorMessage: string | null }>({
        totalSizeMB: 0,  // Track total size of uploaded images
        errorMessage: null,
    });

    // Use ref to track previous existingImagePaths to avoid unnecessary updates
    const prevExistingPathsRef = useRef<string[]>([]);
    
    // State for existing images - maintain local copy to allow deletion
    const [localExistingPaths, setLocalExistingPaths] = useState<string[]>(existingImagePaths);
    const [existingImageUrls, setExistingImageUrls] = useState<{ path: string; url: string }[]>([]);

    // Sync local existing paths with props - only update if actually different
    useEffect(() => {
        const prevPaths = prevExistingPathsRef.current;
        const pathsChanged = existingImagePaths.length !== prevPaths.length || 
                           !existingImagePaths.every((path, index) => path === prevPaths[index]);
        
        if (pathsChanged) {
            setLocalExistingPaths(existingImagePaths);
            prevExistingPathsRef.current = [...existingImagePaths];
        }
    }, [existingImagePaths]);

    // Load existing image URLs when paths change
    useEffect(() => {
        const loadImageUrls = async () => {
            if (localExistingPaths.length === 0) {
                setExistingImageUrls([]);
                return;
            }

            const urlsData = await Promise.all(
                localExistingPaths.map(async path => {
                    const url = await getPictureURL(path, StorageType.AWARENESS_CAMPAIGNS_ADDRESSES);
                    return { path, url: url || '' };
                })
            );
            setExistingImageUrls(urlsData.filter(item => item.url !== ''));
        };

        loadImageUrls();
    }, [localExistingPaths]);

    // Calculate total image count (existing + new)
    const totalImageCount = localExistingPaths.length + images.length;

    // Handle image uploads
    const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newImages = Array.from(files);

            // Check if total images would exceed limit
            if (totalImageCount + newImages.length > MAX_IMAGES) {
                setPickerState((prevState) => ({
                    ...prevState,
                    errorMessage: t('maximum_images_error', { 
                        ns: Namespace.REPORT, 
                        maxImages: MAX_IMAGES, 
                        currentTotal: totalImageCount 
                    }),
                }));
                return;
            }

            // Check for duplicate images (by name and size)
            const duplicateImage = newImages.some((newImage) =>
                images.some((image) => image.name === newImage.name && image.size === newImage.size)
            );
            if (duplicateImage) {
                setPickerState((prevState) => ({
                    ...prevState,
                    errorMessage: t('duplicate_image', { ns: Namespace.REPORT }),
                }));
                return;
            }

            const newImagesSizeMB = newImages.reduce((acc, file) => acc + file.size / (1024 * 1024), 0); // Size in MB

            // Check if adding new images exceeds the total size limit
            if (pickerState.totalSizeMB + newImagesSizeMB > MAX_TOTAL_SIZE_MB) {
                setPickerState((prevState) => ({
                    ...prevState,
                    errorMessage: t('total_size_exceeded', { 
                        ns: Namespace.REPORT, 
                        maxSize: MAX_TOTAL_SIZE_MB 
                    }),
                }));
                return;
            }

            setPickerState((prevState) => ({
                ...prevState,
                errorMessage: null, // Reset error if valid
                totalSizeMB: prevState.totalSizeMB + newImagesSizeMB, // Update total size
            }));

            setImages([...images, ...newImages]);
        }
    }, [images, totalImageCount, pickerState.totalSizeMB, t, setImages]);

    // Handle removing new images and updating the total size
    const handleRemoveNewImage = useCallback((index: number) => {
        const removedImage = images[index];
        const removedImageSizeMB = removedImage.size / (1024 * 1024); // Convert size to MB

        // Update images and total size
        const newImages = images.filter((_, i) => i !== index);
        setImages(newImages);
        setPickerState((prevState) => ({
            ...prevState,
            totalSizeMB: prevState.totalSizeMB - removedImageSizeMB, // Subtract the removed image's size from total
            errorMessage: null,
        }));
    }, [images, setImages]);

    // Handle removing existing images
    const handleRemoveExistingImage = useCallback((path: string) => {
        const updatedPaths = localExistingPaths.filter(p => p !== path);
        setLocalExistingPaths(updatedPaths);
        
        // Notify parent component if callback is provided
        if (onExistingImagesChange) {
            onExistingImagesChange(updatedPaths);
        }
        
        setPickerState((prevState) => ({
            ...prevState,
            errorMessage: null,
        }));
    }, [localExistingPaths, onExistingImagesChange]);

    // Handle click when Add Image button is disabled
    const handleDisabledClick = useCallback(() => {
        if (totalImageCount >= MAX_IMAGES) {
            setPickerState((prevState) => ({
                ...prevState,
                errorMessage: t('maximum_images_error', { 
                    ns: Namespace.REPORT, 
                    maxImages: MAX_IMAGES, 
                    currentTotal: totalImageCount 
                }),
            }));
        }
    }, [totalImageCount, t]);

    // Reusable component for displaying image tiles
    const ImageTile = ({ 
        src, 
        alt, 
        onRemove, 
        isExisting = false 
    }: { 
        src: string; 
        alt: string; 
        onRemove: () => void;
        isExisting?: boolean;
    }) => (
        <Grid item>
            <Box
                position="relative"
                width={100}
                height={100}
                border="1px solid #ddd"
                borderRadius="8px"
            >
                <img
                    src={src}
                    alt={alt}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {isExisting && (
                    <Box
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        bgcolor="rgba(0, 0, 0, 0.5)"
                        p={0.5}
                    >
                        <Typography variant="caption" color="white" align="center" display="block">
                            {t('existing', { ns: Namespace.REPORT })}
                        </Typography>
                    </Box>
                )}
                <IconButton
                    size="small"
                    onClick={onRemove}
                    style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        backgroundColor: 'white',
                        boxShadow: '0px 1px 4px rgba(0,0,0,0.2)',
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>
        </Grid>
    );

    return (
        <FormControl component="fieldset" sx={{ mb: 8 }}>
            <Typography variant="subtitle1" sx={{ my: 1 }}>{t('add_images', { ns: Namespace.REPORT })}</Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('total_images', { ns: Namespace.REPORT })}: {totalImageCount} / {MAX_IMAGES}
                {(existingImageUrls.length > 0 || images.length > 0) && 
                    ` (${existingImageUrls.length} ${t('existing', { ns: Namespace.REPORT })}, ${images.length} ${t('new', { ns: Namespace.REPORT })})`
                }
            </Typography>
            
            <Grid container spacing={2}>
                {/* Display existing images first */}
                {existingImageUrls.map((item, index) => (
                    <ImageTile
                        key={`existing-${item.path}`}
                        src={item.url}
                        alt={`Existing ${index + 1}`}
                        onRemove={() => handleRemoveExistingImage(item.path)}
                        isExisting={true}
                    />
                ))}
                
                {/* Display new images */}
                {images.map((image, index) => (
                    <ImageTile
                        key={`new-${image.name}-${index}`}
                        src={URL.createObjectURL(image)}
                        alt={t('preview', { ns: Namespace.REPORT }) + index}
                        onRemove={() => handleRemoveNewImage(index)}
                        isExisting={false}
                    />
                ))}
                
                {/* Add new image button */}
                <Grid item>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width={100}
                        height={100}
                        border="2px dashed #ddd"
                        borderRadius="8px"
                        style={{ cursor: totalImageCount < MAX_IMAGES ? 'pointer' : 'not-allowed' }}
                        onClick={() => {
                            if (totalImageCount < MAX_IMAGES) {
                                document.getElementById("image-upload")?.click();
                            } else {
                                handleDisabledClick();
                            }
                        }}
                        sx={{ 
                            opacity: totalImageCount >= MAX_IMAGES ? 0.5 : 1,
                            '&:hover': {
                                backgroundColor: totalImageCount < MAX_IMAGES ? 'rgba(0,0,0,0.04)' : 'transparent',
                            }
                        }}
                    >
                        <AddPhotoAlternate style={{ fontSize: '48px', color: '#999' }} />
                    </Box>
                </Grid>
                
                <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    disabled={totalImageCount >= MAX_IMAGES}
                />
            </Grid>
            <Box sx={{ marginTop: 2 }}>
                {pickerState.errorMessage && (
                    <Typography color="error" variant="body2">
                        {pickerState.errorMessage}
                    </Typography>
                )}
                <Typography variant="body2" color="textSecondary" sx={{ marginTop: 2 }}>
                    {t('total_size', { ns: Namespace.REPORT })}: {pickerState.totalSizeMB.toFixed(2)} MB / {MAX_TOTAL_SIZE_MB} MB
                </Typography>
            </Box>
        </FormControl>
    );
};

export default ImagePicker;